// useSynthesisData — the module's single data source. Given a projectId, decides what the panel
// should show by checking the MOST RECENT run's actual outcome first (GET .../runs/latest), THEN
// falling back to the durable stored payload (GET /:projectId) only if the latest run succeeded or
// /runs/latest has nothing live (e.g. aged out of the queue's retention window). This ordering
// matters: an older successful run's payload must never be shown as the default view while masking
// a more recent failure — a project with a good run N-1 followed by a failed run N must surface the
// FAILURE by default, not silently re-show N-1's results with the failure buried in collapsed Run
// History. Until a real synthesis is available to show, source is 'empty' and the module shows an
// onboarding state (or the failed/cancelled/in-flight state via `lastRun`) instead of a demo — it
// never falls back to a demo fixture (that made users think the sample was their own data). The
// bundled fixture is retained only as a structural placeholder so downstream destructuring never
// crashes; it is never rendered while source !== 'live'.

import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../api';
import type { SynthesisPayload } from './types';
import { synthesis as placeholder } from './fixtures';

export interface LastRunInfo {
  jobId: string;
  state: 'failed' | 'cancelled' | 'active' | 'waiting' | 'delayed';
  progress: number;
  error: string | null;
}

export interface RunHistoryEntry {
  jobId: string;
  state: 'completed' | 'failed' | 'cancelled' | 'active' | 'waiting' | 'delayed';
  progress: number;
  error: string | null;
  timestamp: number | null;
  /** Uploaded transcript/finding filenames for this run — shown on a failed/cancelled row so a
   * consultant can tell which upload it was without needing the (nonexistent) payload. */
  files: string[];
}

export interface SynthesisData {
  payload: SynthesisPayload;
  /** 'live' = a real synthesis is loaded; 'empty' = nothing synthesized yet (show the Generate CTA). */
  source: 'live' | 'empty';
  loading: boolean;
  /**
   * Set only when source is 'empty' AND the most recent run for this project did not complete
   * (failed, was cancelled, or is still in flight) — lets the empty state show what actually
   * happened instead of looking identical to "this project has never been run."
   */
  lastRun: LastRunInfo | null;
  /** Recent runs for this project (most-recent first) — powers the run-history list. */
  runHistory: RunHistoryEntry[];
  /** How many transcripts have been ingested for this project (consumer-scoped) — gates whether
   * Run Synthesis is enabled at all. 0 until at least one transcript has been uploaded. */
  sourceCount: number;
  /** True once at least one transcript has been ingested more recently than the last completed
   * synthesis — the signal for showing "Rerun Synthesis" instead of "Run Synthesis". */
  hasUnsynthesizedSources: boolean;
  /** Swap the rendered payload in place (e.g. right after an Import/Generate) without a re-fetch. */
  applyPayload: (p: SynthesisPayload) => void;
  /** Re-run the same load this hook does on mount — used by the empty state's Retry affordance. */
  refresh: () => void;
}

export function useSynthesisData(projectId?: string | null): SynthesisData {
  const [payload, setPayload] = useState<SynthesisPayload>(placeholder);
  const [source, setSource] = useState<'live' | 'empty'>('empty');
  const [loading, setLoading] = useState(!!projectId);
  const [lastRun, setLastRun] = useState<LastRunInfo | null>(null);
  const [runHistory, setRunHistory] = useState<RunHistoryEntry[]>([]);
  const [sourceCount, setSourceCount] = useState(0);
  const [hasUnsynthesizedSources, setHasUnsynthesizedSources] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!projectId) {
      setSource('empty'); setLastRun(null); setRunHistory([]); setLoading(false);
      setSourceCount(0); setHasUnsynthesizedSources(false);
      return;
    }
    let alive = true;
    // Timer id for the in-flight reconnect poll below (set only when a run is still running at
    // mount). Cleared on unmount / projectId change so a background run's poll never outlives the
    // view that started it.
    let pollTimer: number | null = null;
    setLoading(true);

    // Run history is fetched regardless of whether a live payload exists — a project with a stored
    // synthesis can still have earlier failed/cancelled runs worth showing. Best-effort: a failure
    // here never blocks the main payload load.
    (async () => {
      try {
        const histRes = await apiClient.get(`/api/synthesis-viz/${encodeURIComponent(projectId)}/runs`);
        if (!alive) return;
        const list = Array.isArray(histRes?.data?.runs) ? histRes.data.runs : [];
        setRunHistory(list as RunHistoryEntry[]);
      } catch {
        if (alive) setRunHistory([]);
      }
    })();

    // Ingested-source signal is fetched on the same tick as everything else so it invalidates
    // together with runHistory/lastRun/payload after any upload or synthesis run completes.
    // Best-effort: a failure here never blocks the main payload load — the Run/Rerun button just
    // stays conservatively disabled (sourceCount defaults to 0) rather than crashing the panel.
    (async () => {
      try {
        const srcRes = await apiClient.get(`/api/synthesis-viz/${encodeURIComponent(projectId)}/ingested-sources`);
        if (!alive) return;
        setSourceCount(typeof srcRes?.data?.sourceCount === 'number' ? srcRes.data.sourceCount : 0);
        setHasUnsynthesizedSources(!!srcRes?.data?.hasUnsynthesizedSources);
      } catch {
        if (alive) { setSourceCount(0); setHasUnsynthesizedSources(false); }
      }
    })();

    (async () => {
      try {
        // Check the MOST RECENT run's actual outcome first, unconditionally — not just when there's
        // no stored payload. An older successful run's payload must never be shown as the default
        // view while masking a more recent failure: that was exactly the reported bug (run N-1
        // succeeds, run N fails, reopening the project silently shows N-1's results with the failure
        // buried inside collapsed Run History instead of surfaced up front).
        let latest: any = null;
        try {
          const runRes = await apiClient.get(`/api/synthesis-viz/${encodeURIComponent(projectId)}/runs/latest`);
          latest = runRes?.data || null;
        } catch {
          latest = null; // best-effort — fall through to the stored-payload path below
        }
        if (!alive) return;

        if (latest && latest.state && latest.state !== 'none' && latest.state !== 'completed') {
          // The most recent run did NOT succeed (failed / cancelled / still in flight) — show that
          // state as the default view. Do not fall back to an older stored payload; the empty-state
          // branch in the component reads `lastRun` to render the failed/cancelled/in-flight panel.
          setSource('empty');
          setLastRun({
            jobId: String(latest.jobId || ''),
            state: latest.state,
            progress: typeof latest.progress === 'number' ? latest.progress : 0,
            error: latest.error || null,
          });
          // If the run is still IN FLIGHT (not a settled failure/cancellation), reconnect to it:
          // poll until it reaches a terminal state, then refresh so the completed result — and the
          // Run History badge — update on their own. Without this, a run that was already running
          // when the panel mounted (page reload, or opened from another device) would finish in the
          // background but keep showing "Running"/"in progress" until a manual reload / "Check again"
          // (client report 2026-09-29 — "finished running but still showing as processing"). The
          // in-session poll in SynthesisVisualizations.handleSynthesize only covers a run THIS
          // session started, which is why the reconnect has to live here, on mount.
          const IN_FLIGHT = new Set(['active', 'waiting', 'delayed']);
          if (IN_FLIGHT.has(latest.state) && latest.jobId) {
            const jobId = String(latest.jobId);
            const poll = async () => {
              if (!alive) return;
              let st: any = null;
              try {
                const r = await apiClient.get(`/api/synthesis-viz/${encodeURIComponent(projectId)}/run/${encodeURIComponent(jobId)}`);
                st = r?.data || null;
              } catch {
                // transient error — keep polling; a real terminal state will still arrive.
              }
              if (!alive) return;
              if (st && typeof st.progress === 'number') {
                setLastRun((prev) => (prev && prev.jobId === jobId ? { ...prev, progress: st.progress } : prev));
              }
              if (st && st.state && !IN_FLIGHT.has(st.state)) {
                // Terminal (completed / failed / cancelled / unknown) — reload the whole view so the
                // payload, lastRun, and runHistory all reflect the finished run. clearing the timer
                // first so the reload's own fetches don't race a queued poll.
                if (pollTimer != null) { clearTimeout(pollTimer); pollTimer = null; }
                setRefreshTick((t) => t + 1);
                return;
              }
              pollTimer = window.setTimeout(poll, 4000);
            };
            pollTimer = window.setTimeout(poll, 4000);
          }
          return;
        }

        // Most recent run either succeeded or /runs/latest found nothing live (e.g. the job aged out
        // of the queue's retention window, or this project's only history predates that endpoint) —
        // fall back to the durable stored snapshot, which survives queue expiry.
        const res = await apiClient.get(`/api/synthesis-viz/${encodeURIComponent(projectId)}`);
        const live = res?.data?.payload;
        if (!alive) return;
        if (live && typeof live === 'object' && Array.isArray(live.themes)) {
          setPayload(live as SynthesisPayload);
          setSource('live');
          setLastRun(null);
        } else {
          setSource('empty');
          setLastRun(null);
        }
      } catch {
        // No route / no snapshot / auth — show the empty state, never a crash.
        if (alive) { setSource('empty'); setLastRun(null); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (pollTimer != null) { clearTimeout(pollTimer); pollTimer = null; }
    };
  }, [projectId, refreshTick]);

  const applyPayload = useCallback((p: SynthesisPayload) => {
    setPayload(p);
    setSource('live');
    setLastRun(null);
    setLoading(false);
  }, []);

  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  return {
    payload, source, loading, lastRun, runHistory,
    sourceCount, hasUnsynthesizedSources,
    applyPayload, refresh,
  };
}
